import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BookOpen, Microscope, Syringe, ShieldCheck, Pill, Apple, Brain, HelpCircle, Activity, AlertTriangle, Languages } from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Button, Badge, Alert, LoadingSkeleton, EmptyState, PullQuote } from '../../components/portal/ui'
import { HumanArt } from '../../components/portal/HumanArt'
import { usePublishedTestimonials, testimonialAttribution } from '../../components/portal/Voices'
import { getPublishedArticles, type KnowledgeArticle } from '../../services/portalApi'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'

// Non-text meta for the topic grid (icon + anchor). Text (title/desc) is zipped
// in from `pages.aboutTb.topics` so it renders in the active language.
const TOPIC_META = [
  { icon: BookOpen, anchor: 'what-is-tb' },
  { icon: Activity, anchor: 'symptoms' },
  { icon: Microscope, anchor: 'diagnosis' },
  { icon: Pill, anchor: 'treatment' },
  { icon: ShieldCheck, anchor: 'prevention' },
  { icon: AlertTriangle, anchor: 'drug-resistant' },
  { icon: Apple, anchor: 'nutrition' },
  { icon: Brain, anchor: 'wellbeing' },
  { icon: HelpCircle, anchor: 'myths' },
  { icon: Syringe, anchor: 'vaccine' },
  { icon: Languages, anchor: 'words' },
]

/**
 * Plain-language glossary for government / clinical TB terminology.
 * Pattern: plain phrase first, official term shown as a muted badge, then a
 * short reassuring explanation. General public-health meaning only — no
 * programme statistics are stated. Definitions are educational and do not
 * replace official programme guidance.
 * The glossary text (plain/official/explain) is authored in the i18n locales
 * (`pages.aboutTb.words.*`); this component only renders it.
 */

export default function AboutTBPage() {
  const { t, i18n } = useTranslation()
  const [articles, setArticles] = useState<KnowledgeArticle[]>([])
  const [loading, setLoading] = useState(true)
  // A real voice leads the page — human story first, never fabricated.
  const { voices } = usePublishedTestimonials(3)
  const voice = voices[0]

  const topics = t('pages.aboutTb.topics', { returnObjects: true }) as { title: string; desc: string }[]
  const words = t('pages.aboutTb.words.items', { returnObjects: true }) as { plain: string; official: string; explain: string }[]
  const factLabels = t('pages.aboutTb.facts.labels', { returnObjects: true }) as string[]
  const factValue = t('pages.aboutTb.facts.value')

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const res = await getPublishedArticles(0, 12)
        if (active) setArticles(res.content ?? [])
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [])

  return (
    <PortalLayout>
      <Seo
        title={t('pages.aboutTb.seo.title')}
        description={t('pages.aboutTb.seo.description')}
        canonicalPath="/about-tb"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.aboutTb', 'About TB'), path: '/about-tb' }]}
      />
      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.aboutTb', 'About TB') }]} />
          <div className="grid lg:grid-cols-2 gap-8 items-center mt-2">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl bg-warm-100/60 border border-warm-200 p-6">
                <HumanArt variant="joined-hands" className="w-full h-auto max-w-sm mx-auto" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              {voice ? (
                (() => {
                  const { author, role } = testimonialAttribution(voice)
                  return <PullQuote quote={voice.body} author={author} role={role} to="/stories" linkLabel={t('common.readStory')} className="mb-6" />
                })()
              ) : null}
              <SectionHeading as="h1" eyebrow={t('pages.aboutTb.hero.eyebrow')} title={t('pages.aboutTb.hero.title')} description={t('pages.aboutTb.hero.description')} />
            </div>
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-4">
        <Container>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {TOPIC_META.map((tp, i) => {
              const Icon = tp.icon
              const label = topics[i]?.title ?? ''
              return (
                <a key={tp.anchor} href={`#${tp.anchor}`} className="card-hover p-5 text-center group">
                  <span className="inline-flex w-11 h-11 rounded-xl bg-[rgb(232_241_252)] text-[rgb(15_76_129)] items-center justify-center mb-3" aria-hidden="true">
                    <Icon className="w-5 h-5" />
                  </span>
                  <p className="text-sm font-semibold text-snow-900">{label}</p>
                </a>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* TB words in plain language — glossary */}
      <Section id="words" className="bg-white">
        <Container>
          <SectionHeading
            eyebrow={t('pages.aboutTb.words.eyebrow')}
            title={t('pages.aboutTb.words.title')}
            description={t('pages.aboutTb.words.description')}
          />
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {words.map((w) => (
              <Card key={w.official} className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-snow-900">{w.plain}</h3>
                  <Badge tone="slate" className="shrink-0">{w.official}</Badge>
                </div>
                <p className="text-sm text-snow-600 text-pretty">{w.explain}</p>
              </Card>
            ))}
          </div>
          <p className="text-xs text-snow-500 mt-6">
            {t('pages.aboutTb.words.footnote')}
          </p>
        </Container>
      </Section>

      {/* Knowledge articles from backend */}
      <Section className="bg-snow-50">
        <Container>
          <SectionHeading eyebrow={t('pages.aboutTb.knowledge.eyebrow')} title={t('pages.aboutTb.knowledge.title')} description={t('pages.aboutTb.knowledge.description')} />
          {loading ? (
            <LoadingSkeleton count={4} />
          ) : articles.length ? (
            <>
              {articles.some((a) => a.matchedLanguage && a.matchedLanguage !== (i18n.language?.split('-')[0] || 'en')) && (
                <TranslatedContentNotice matchedLanguage="en" />
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.map((a) => (
                  <Card key={a.id} hover className="p-5 flex flex-col">
                    <Badge tone="blue" className="mb-2 self-start">{a.categoryName ?? t('common.knowledge', 'Knowledge')}</Badge>
                    <h3 className="font-semibold text-snow-900 mb-1.5">{a.title}</h3>
                    {a.excerpt && <p className="text-sm text-snow-600 line-clamp-3 flex-1">{a.excerpt}</p>}
                    <Link to="/knowledge" className="text-sm font-medium text-[rgb(15_76_129)] hover:underline mt-3">
                      {t('pages.aboutTb.knowledge.readArticle')}
                    </Link>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <EmptyState icon={<BookOpen className="w-7 h-7" />} title={t('pages.aboutTb.knowledge.emptyTitle')} description={t('pages.aboutTb.knowledge.emptyDesc')} />
          )}
          <div className="mt-6">
            <Button to="/knowledge" variant="outline">{t('pages.aboutTb.knowledge.openHub')}</Button>
          </div>
        </Container>
      </Section>

      {/* Cross-link to I Have TB */}
      <Section className="bg-[rgb(240_252_255)]/60 pt-0">
        <Container>
          <Card className="p-8 bg-gradient-to-br from-[rgb(232_241_252)] to-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-snow-900">{t('pages.aboutTb.crossLink.title')}</h2>
                <p className="text-snow-600 mt-1">{t('pages.aboutTb.crossLink.body')}</p>
              </div>
              <Button to="/i-have-tb" variant="primary">{t('pages.aboutTb.crossLink.cta')}</Button>
            </div>
          </Card>
        </Container>
      </Section>

      {/* DATA TIER — at the bottom, clearly labeled. National statistics require an official
          source; no figures are shown until verified, to avoid inaccuracy. */}
      <Section id="facts" className="bg-snow-50">
        <Container>
          <SectionHeading eyebrow={t('pages.aboutTb.facts.eyebrow')} title={t('pages.aboutTb.facts.title')} description={t('pages.aboutTb.facts.description')} />
          <Alert tone="warning" title={t('pages.aboutTb.facts.alertTitle')}>
            {t('pages.aboutTb.facts.alertBody')}
          </Alert>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {factLabels.map((label) => (
              <Card key={label} className="p-5">
                <p className="text-sm text-snow-500">{label}</p>
                <p className="text-lg font-semibold text-snow-500 mt-1">{factValue}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>
    </PortalLayout>
  )
}